import { Flex, type FlexProps, IconButton } from "@chakra-ui/react"
import { useForm } from "react-hook-form"
import { useIntl } from "react-intl"
import { useSearchParams } from "react-router-dom"

import { MultiplyIcon } from "@/components/Icons"

import { SearchButton } from "./SearchButton"
import { SearchbarInput } from "./SearchbarInput"
import { SEARCH_PARAM_NAME } from "./constants"

type SearchFormInput = {
  searchTerm: string
}

type Props = {
  searchbarPlaceholder: string
  submitButtonAriaLabel: string
  // Shows a cross in the field, while it holds text, that empties it and removes the search.
  clearable?: boolean
} & FlexProps

export const Searchbar: React.FC<Props> = ({
  searchbarPlaceholder,
  submitButtonAriaLabel,
  clearable = false,
  ...flexProps
}) => {
  const intl = useIntl()
  const [searchParams, setSearchParams] = useSearchParams()
  const search = searchParams.get(SEARCH_PARAM_NAME) ?? undefined

  const currentSearchTerm = search

  const { register, handleSubmit, setValue, watch } = useForm<SearchFormInput>({
    defaultValues: { searchTerm: currentSearchTerm ?? "" },
  })
  const typedTerm = watch("searchTerm")

  const handleSearchSubmit = ({ searchTerm }: SearchFormInput) => {
    const newSearchParams = new URLSearchParams(searchParams as URLSearchParams)
    newSearchParams.set(SEARCH_PARAM_NAME, searchTerm)
    if (setSearchParams) setSearchParams(newSearchParams)
  }

  const clearSearch = () => {
    setValue("searchTerm", "")
    const newSearchParams = new URLSearchParams(searchParams as URLSearchParams)
    newSearchParams.delete(SEARCH_PARAM_NAME)
    setSearchParams(newSearchParams)
  }

  return (
    <Flex onSubmit={handleSubmit(handleSearchSubmit)} as="form" gap="3" {...flexProps}>
      <SearchbarInput
        placeholder={searchbarPlaceholder}
        {...register("searchTerm")}
        rightElement={
          clearable && (typedTerm || search) ? (
            <IconButton
              type="button"
              variant="transparent"
              size="xs"
              minW="6"
              h="6"
              aria-label={intl.formatMessage({ id: "search.clear" })}
              icon={<MultiplyIcon boxSize="4" />}
              onClick={clearSearch}
            />
          ) : undefined
        }
      />
      <SearchButton ariaLabel={submitButtonAriaLabel} />
    </Flex>
  )
}
